// src/PasswordRequirements.js
//
// Şifre kuralları paneli. Bu ekranın kalbi: log analizine göre kullanıcıların
// %87'si şifre değiştirmede başarısız oluyordu ve sebebini göremiyordu.
//
// İki grup var ve AYNI görsel dili kullanırlar; farkları başlıklarında yazar:
//   ZORUNLU              -> hepsi sağlanmalı
//   KARAKTER ÇEŞİTLİLİĞİ -> 4 gruptan en az 3'ü (biri eksik kalabilir)
// Daha önce ikisi farklı görünüyordu ve kullanıcılar noktalamayı zorunlu
// sanıyordu. Noktalama AD'de de zorunlu değildir, seçeneklerden biridir -
// listeden çıkarmak kuralı SIKILAŞTIRIR (geriye 3 grup kalır, üçü de zorunlu
// olur), o yüzden seçenek olarak duruyor ve "isteğe bağlı" etiketi taşıyor.
//
// Renk mantığı: "henüz yapılmadı" (nötr) ile "yanlış yaptın" (kırmızı) ayrıdır.
// Boş bir şifre yedi kırmızı blok üretmemeli.
//
// Kurallar backend'deki app/password_policy.py ile birebir aynı olmalıdır.

import React from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { tokens } from './theme';

const SPECIAL_CHARS = '!"#$%&\'()*+,-./:;<=>?@[\\]^_`{|}~';
const TURKISH_CHARS = 'çÇğĞıİöÖşŞüÜ';

function normalize(value) {
  if (!value) return '';
  const map = { ç: 'c', ğ: 'g', ı: 'i', i: 'i', İ: 'i', ö: 'o', ş: 's', ü: 'u' };
  return value.toLowerCase().split('').map((ch) => map[ch] || ch).join('');
}

// AD karmaşıklık kuralı token'ı BÜTÜN olarak arar, parçalarını değil.
// (Eskiden 3 karakterlik kayan pencere kullanılıyordu; numarası 20231234 olan
// bir öğrenci için "Lhu2026*" bile "202" yüzünden reddediliyordu.)
function containsToken(password, token, minLen = 3) {
  const t = normalize(token);
  const p = normalize(password);
  if (!t || t.length < minLen) return false;
  return p.includes(t);
}

function turkishCharsUsed(password) {
  return [...new Set([...password].filter((ch) => TURKISH_CHARS.includes(ch)))];
}

// Tek satırlık gereksinim rozeti. Dolu arka plan + kalın yazı: tamamlananlar
// bir bakışta ayırt edilsin, mobilde de okunaklı kalsın.
function Pill({ state, label, tag }) {
  const conf = {
    ok:        { bg: tokens.success, fg: '#ffffff',       Icon: CheckCircleIcon,          border: tokens.success },
    violation: { bg: tokens.danger,  fg: '#ffffff',       Icon: ErrorIcon,                border: tokens.danger },
    todo:      { bg: tokens.canvas,  fg: tokens.inkMuted, Icon: RadioButtonUncheckedIcon, border: tokens.line },
  }[state];
  const { Icon } = conf;
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1,
      px: 1.25, py: 0.9, borderRadius: 1.5,
      backgroundColor: conf.bg,
      border: `1px solid ${conf.border}`,
      transition: 'background-color 160ms ease, border-color 160ms ease',
    }}>
      <Icon sx={{ fontSize: '1.05rem', color: conf.fg, flexShrink: 0 }} />
      <Typography sx={{
        fontSize: '0.85rem', color: conf.fg, lineHeight: 1.35,
        fontWeight: state === 'todo' ? 500 : 600, flex: 1,
      }}>
        {label}
      </Typography>
      {tag && (
        <Typography sx={{
          fontSize: '0.68rem', color: conf.fg, opacity: 0.85,
          whiteSpace: 'nowrap', fontWeight: 500,
        }}>
          {tag}
        </Typography>
      )}
    </Box>
  );
}

function SectionLabel({ title, hint }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.75, mt: 1.5, flexWrap: 'wrap' }}>
      <Typography sx={{
        fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.09em',
        textTransform: 'uppercase', color: tokens.inkMuted,
      }}>
        {title}
      </Typography>
      {hint && (
        <Typography sx={{ fontSize: '0.72rem', color: tokens.inkFaint }}>
          — {hint}
        </Typography>
      )}
    </Box>
  );
}

function PasswordRequirements({ password = '', username = '', name = '', surname = '', tcNo = '', showTc = false }) {
  const { t } = useTranslation();
  const typed = password.length > 0;
  const usedTurkish = turkishCharsUsed(password);

  const digitsOnly = password.replace(/\D/g, '');
  const tcHit = Boolean(
    showTc && tcNo && tcNo.length >= 4 &&
    (digitsOnly.includes(tcNo) || digitsOnly.includes(tcNo.slice(0, 5)) || digitsOnly.includes(tcNo.slice(-5)))
  );

  const rules = [
    { ok: password.length >= 8, violated: false, label: t('passwordRequirementMinLength') },
    {
      ok: typed && usedTurkish.length === 0,
      violated: usedTurkish.length > 0,
      label: usedTurkish.length > 0
        ? `${t('reqTurkishUsed')}: ${usedTurkish.join(' ')}`
        : t('reqNoTurkish'),
    },
    { ok: typed && !password.includes(' '), violated: password.includes(' '), label: t('reqNoSpace') },
    {
      ok: Boolean(username) && !containsToken(password, username),
      violated: Boolean(username) && containsToken(password, username),
      label: t('reqNoUsernameShort'), skip: !username,
    },
    {
      ok: Boolean(name) && !containsToken(password, name),
      violated: Boolean(name) && containsToken(password, name),
      label: t('reqNoNameShort'), skip: !name,
    },
    {
      ok: Boolean(surname) && !containsToken(password, surname),
      violated: Boolean(surname) && containsToken(password, surname),
      label: t('reqNoSurnameShort'), skip: !surname,
    },
    {
      ok: showTc && Boolean(tcNo) && !tcHit,
      violated: tcHit,
      label: t('reqNoTcShort'), skip: !showTc || !tcNo,
    },
  ].filter((r) => !r.skip);

  const categories = [
    { name: t('catUpper'), ok: /[A-Z]/.test(password) },
    { name: t('catLower'), ok: /[a-z]/.test(password) },
    { name: t('catDigit'), ok: /\d/.test(password) },
    { name: t('catPunct'), ok: [...password].some((ch) => SPECIAL_CHARS.includes(ch)), optional: true },
  ];
  const categoryCount = categories.filter((c) => c.ok).length;
  const categoryOk = categoryCount >= 3;

  const allPassed = typed && rules.every((r) => r.ok) && categoryOk;
  const passedCount = rules.filter((r) => r.ok).length + (categoryOk ? 1 : 0);
  const totalCount = rules.length + 1;
  const hasViolation = rules.some((r) => r.violated);

  const gridSx = { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 };

  return (
    <Box sx={{
      border: '2px solid',
      borderColor: allPassed ? tokens.success : hasViolation ? tokens.danger : tokens.line,
      backgroundColor: tokens.surface,
      p: 2, borderRadius: 2, mb: 2,
      transition: 'border-color 200ms ease',
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ color: tokens.ink, fontWeight: 700 }}>
          {t('passwordRequirementsTitle')}
        </Typography>
        <Typography variant="caption" sx={{
          color: allPassed ? tokens.success : tokens.inkMuted, fontWeight: 700,
        }}>
          {passedCount}/{totalCount}
        </Typography>
      </Box>

      <LinearProgress
        variant="determinate"
        value={(passedCount / totalCount) * 100}
        sx={{
          height: 7, borderRadius: 4, backgroundColor: tokens.canvas,
          '& .MuiLinearProgress-bar': {
            borderRadius: 4,
            backgroundColor: allPassed ? tokens.success : hasViolation ? tokens.danger : tokens.brand,
          },
        }}
      />

      <SectionLabel title={t('sectionRequired')} />
      <Box sx={gridSx}>
        {rules.map((r, i) => (
          <Pill key={i} label={r.label} state={r.violated ? 'violation' : r.ok ? 'ok' : 'todo'} />
        ))}
      </Box>

      {/* Aynı görsel dil; fark başlıkta yazıyor: burada 4'ten 3'ü yeterli. */}
      <SectionLabel title={t('sectionVariety')} hint={`${t('varietyHint')} · ${categoryCount}/4`} />
      <Box sx={gridSx}>
        {categories.map((c) => (
          <Pill
            key={c.name}
            label={c.name}
            state={c.ok ? 'ok' : 'todo'}
            tag={c.optional && !c.ok ? t('optionalTag') : null}
          />
        ))}
      </Box>

      {typed && (
        allPassed ? (
          <Box sx={{
            mt: 2, p: 1.25, borderRadius: 1.5, backgroundColor: tokens.success,
            display: 'flex', alignItems: 'center', gap: 1,
          }}>
            <CheckCircleIcon sx={{ color: '#fff', fontSize: '1.2rem' }} />
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.9rem' }}>
              {t('passwordRequirementAllPassed')}
            </Typography>
          </Box>
        ) : (
          <Box sx={{
            mt: 2, p: 1.25, borderRadius: 1.5,
            backgroundColor: tokens.dangerWash, border: `1px solid ${tokens.danger}`,
            display: 'flex', alignItems: 'flex-start', gap: 1,
          }}>
            <ErrorIcon sx={{ color: tokens.danger, fontSize: '1.2rem', flexShrink: 0, mt: '1px' }} />
            <Typography sx={{ color: tokens.danger, fontWeight: 600, fontSize: '0.85rem', lineHeight: 1.45 }}>
              {t('notReadyWarning')}
            </Typography>
          </Box>
        )
      )}
    </Box>
  );
}

export default PasswordRequirements;
